import React, { useEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import UIcon from "../../../lib/icons/untitled_ui/UIcon";
import { fadeIn } from "../../../styles/animations";

type PositionType = "center-center" | "top-center";
export type ModalProps = {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  width?: string;
  maxWidth?: string;
  position?: PositionType;
  offsetTop?: string;
  showCloseButton?: boolean;
  backgroundClickClose?: boolean;
  noContentBackground?: boolean;
  padding?: number;
  backgroundColor?: string;
};

/**
 * @param children - The content of the modal
 * @param open - Whether the modal is open or not
 * @param onOpenChange - Function which is called when the open state changes. Will be called with the new open state as a boolean
 * @param width - Explicit width of the modal. By default a `<dialog>` sizes to its content (capped by `maxWidth`); set this when you need the modal to be a fixed width regardless of content.
 * @param maxWidth - Max width of the modal
 * @param position - Default "center-center". Position of the modal
 * @param offsetTop - Pins the modal this far from the top of the viewport (e.g. "80px") instead of `position`, so a change in its height only moves its bottom edge.
 * @param showCloseButton - Default true. Whether to show a close button in the top right corner (content if noContentBackground is false, page if true).
 * @param backgroundClickClose - Default true. Whether if the modal should close when clicking on the background of the page (outside the modal content)
 * @param noContentBackground - Default false. Whether the modal content should have a background. Set to true if you need something very custom (e.g. video player)
 * @param padding - Default to 16px when noContentBackground is false.
 * @param backgroundColor - Background color of the modal. If not set, it will use the default theme color.
 */
const Modal = ({
  children,
  open,
  onOpenChange,
  width,
  maxWidth = "calc(100vw - 60px)",
  position = "center-center",
  offsetTop,
  showCloseButton = true,
  backgroundClickClose = true,
  noContentBackground = false,
  padding,
  backgroundColor,
}: ModalProps) => {
  const [shouldRender, setShouldRender] = useState(open);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const skipDefaultClose = useRef(false);

  useEffect(() => {
    if (!dialogRef.current) return;
    if (open) {
      setShouldRender(true);

      // If state has desynced somehow (should just be from hot-reload)
      if (dialogRef.current.hasAttribute("open")) {
        console.warn("Modal is already open. Closing it before opening again.");
        // Close it first so if it's not a modal it can be made one (can't do .showModal() if it's already open)
        // Wish you could detect if it's in modal mode or not but I couldn't find a way (should be possible via aria-modal but it didn't work)
        dialogRef.current.close();
      }
      dialogRef.current.showModal();
    } else {
      // If a browser doesn't support overlay, the exit transition looks very bad
      // & onTransitionEnd will not run, therefore we need to skip the animation all together
      if (!CSS.supports("overlay", "auto")) {
        setShouldRender(false);
      }
      dialogRef.current.close();
    }
  }, [open]);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (backgroundClickClose && e.target === e.currentTarget) {
      skipDefaultClose.current = true;
      onOpenChange(false);
    }
  };

  return (
    <Dialog
      onMouseDown={handleBackgroundClick} // Not using onClick so if user selects text and drags into the background area it won't trigger
      onClose={(e) => {
        e.stopPropagation(); // Allows multiple dialog using components to be nested

        // onOpenChange is called when the modal is closed, but we want to skip it if the close was
        // triggered by the close button or a background click as otherwise it will be called twice
        if (skipDefaultClose.current) {
          skipDefaultClose.current = false;
          return;
        }
        onOpenChange(false);
      }}
      onClick={(e) => e.stopPropagation()} // Stop click events from propagating to the parent
      ref={dialogRef}
      noContentBackground={noContentBackground}
      width={width}
      maxWidth={maxWidth}
      position={position}
      offsetTop={offsetTop}
      onTransitionEnd={(e) => {
        if (!open && e.target === e.currentTarget) {
          setShouldRender(false);
        }
      }}
      aria-hidden={shouldRender ? undefined : true} // aria-hidden=false isn't treated correct by some software, it just needs to not be present
      data-modal=""
    >
      {shouldRender ? (
        <DialogWrapper
          noContentBackground={noContentBackground}
          padding={padding}
          backgroundColor={backgroundColor}
        >
          {children}
          {showCloseButton && (
            <CloseModalButton
              type="button" // Default is submit which would submit an enclosing form
              onClick={() => {
                skipDefaultClose.current = true;
                onOpenChange(false);
              }}
              location={noContentBackground ? "page" : "modal"}
              aria-label="Close"
            >
              <UIcon icon="xClose" size={noContentBackground ? 32 : 16} color="inherit" />
            </CloseModalButton>
          )}
        </DialogWrapper>
      ) : null}
    </Dialog>
  );
};

const fadeInBackground = keyframes({
  from: { opacity: 0 },
  to: { opacity: 0.5 },
});

function modalMargin(position: PositionType, offsetTop?: string): string {
  if (offsetTop) return `${offsetTop} auto auto`;
  return position === "center-center" ? "auto" : "5% auto";
}

const Dialog = styled.dialog<{
  noContentBackground: boolean;
  width?: string;
  maxWidth: string;
  position: PositionType;
  offsetTop?: string;
}>(({ theme, noContentBackground, position, offsetTop, width, maxWidth }) => ({
  // Disabling border + padding so when backgroundClickClose is true, it doesn't trigger from clicking on the border or padding
  borderWidth: 0,
  padding: 0,
  ...(width ? { width } : {}),
  maxWidth,
  maxHeight: "calc(100vh - 26px)",
  minWidth: "300px",
  cursor: "auto", // Cursor might be set on the parent of the Modal but shouldn't go to the modal contents

  margin: modalMargin(position, offsetTop),

  // Using both animation and transition, since animation runs when modal is opened while transition runs when modal is closed
  // Should be possible to only use one of them but I couldn't get it to work without side effects (namely dialog contents being visible inline on the page)
  animation: `${fadeIn} 0.3s ease-out`,
  transition:
    "display 300ms allow-discrete ease-out, overlay 300ms allow-discrete ease-out, opacity 300ms ease-out",
  opacity: 0,
  "&::backdrop": {
    animation: `${fadeInBackground} 300ms ease-out`,
    transition: "background-color 300ms ease-out, opacity 300ms ease-out",
    backgroundColor: "transparent",
    opacity: 0,
  },
  "&:focus-visible": {
    // Should autofocus on first focusable element inside the modal, so this style shouldn't be needed
    // but in reality some dialogs for some reason get focused instead, so we need to disable the outline
    outline: "none",
  },
  "&[open]": {
    opacity: 1,
    "&::backdrop": {
      backgroundColor: theme.colors.sysBackgroundOverlay,
      opacity: 0.5,
    },
  },

  ...(noContentBackground
    ? {
        backgroundColor: "transparent",
      }
    : {
        borderRadius: "8px",
      }),
}));

const DialogWrapper = styled.div<{
  noContentBackground: boolean;
  padding?: number;
  backgroundColor?: string;
}>(({ noContentBackground, padding, backgroundColor }) => ({
  padding: padding ?? (noContentBackground ? undefined : 16),
  backgroundColor,
}));

const CloseModalButton = styled.button<{ location: "page" | "modal" }>(({ theme, location }) => ({
  position: location === "modal" ? "absolute" : "fixed",
  top: "0px",
  right: "0px",
  padding: "16px",
  color: theme.colors.sysTextSecondary,
  backgroundColor: "transparent",
  border: "none",
  lineHeight: 0,
  cursor: "pointer",
  zIndex: 2,
  "&:hover": {
    color: theme.colors.sysTextBlack,
  },
}));

export default Modal;
